import { LitElement } from '@lit'
import { viewportObserver } from '@nova/utils'

class NovaProductGallery extends LitElement {
  static properties = {
    viewport: { type: String, state: true },
    galleryCarousel: { type: Object, state: true },
    zoomInOverlay: { type: Object, state: true },
    progressFill: { type: Object, state: true },
    totalSlides: { type: Number, state: true },
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.galleryCarousel = null
    this.zoomInOverlay = null
    this.observer = null
    this.progressFill = null
    this.totalSlides = 0
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    viewportObserver.removeListener(this._handleViewportChange)
    document.removeEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  updated() {
    this.galleryCarousel = this.querySelector('.js-gallery-carousel')
    this.progressFill = this.querySelector('.product__carousel-progress-fill')
    this.totalSlides = parseInt(this.querySelector('.product__carousel-item-count').value)

    // Only initialize carousel functionality if carousel exists (mobile view)
    if (this.galleryCarousel) {
      this.handleGalleryCarouselMove()
      this.resetCarousel()
      this.updateProgressBar(0)
      this.galleryCarousel.goToSlide(0)
    }

    // Initialize zoom functionality for both carousel and desktop gallery
    this.handleZoomOnImage(3)

    this.initializeZoomModal()
  }

  resetCarousel() {
    const carousels = this.querySelectorAll('sl-carousel')
    for (const carousel of carousels) {
      carousel.goToSlide(0)
    }
  }

  updateGalleryContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
  }

  handleGalleryCarouselMove() {
    if (!this.galleryCarousel) return

    this.galleryCarousel.addEventListener('sl-slide-change', (event) => {
      this.handleVideoAutoPlay(event.detail.slide)
      this.updateProgressBar(event.detail.index)
    })
  }

  updateProgressBar(currentIndex) {
    if (!this.progressFill || this.totalSlides === 0) return

    // Update progress bar fill
    const progressPercentage = ((currentIndex + 1) / this.totalSlides) * 100
    this.progressFill.style.width = `${progressPercentage}%`
  }

  handleVideoAutoPlay(slide) {
    const media = slide.querySelector('nova-adaptive-video')
    if (!media) return
    media.play()
  }

  zoomMoveWithHover(image, event, zoomRatio) {
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    this.zoomInOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.zoomInOverlay.style.backgroundSize = `${image.width * zoomRatio}px`
  }

  handleZoomEvents(image, zoomRatio) {
    this.zoomInOverlay = image.closest('.img').querySelector('.zoom-overlay')
    if (!this.zoomInOverlay) return
    this.zoomInOverlay.classList.remove('hidden')

    const carouselItem = image.closest('.gallery-item')
    if (carouselItem && !carouselItem.classList.contains('zoomed'))
      carouselItem.classList.add('zoomed')

    this.zoomInOverlay.addEventListener(
      'click',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
    this.zoomInOverlay.addEventListener('mousemove', (event) =>
      this.zoomMoveWithHover(image, event, zoomRatio),
    )
    this.zoomInOverlay.addEventListener(
      'mouseleave',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
  }

  handleOverlayRemove(carouselItem) {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')

      this.zoomInOverlay.removeEventListener(
        'click',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
      this.zoomInOverlay.removeEventListener(
        'mousemove',
        this.zoomMoveWithHover,
      )
      this.zoomInOverlay.removeEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, carouselItem),
      )

      this.zoomInOverlay = null
    }
    if (carouselItem) carouselItem.classList.remove('zoomed')
  }

  handleZoomOnImage(zoomRatio) {
    const images = this.querySelectorAll('.zoom-image')
    for (const image of images) {
      image.addEventListener('click', () => {
        this.handleZoomEvents(image, zoomRatio)
      })
    }
  }

  initializeZoomModal() {
    setTimeout(() => {
      const mobileCarousel = this.querySelector('.js-gallery-carousel')
      if (mobileCarousel) {
        const mobileImages = mobileCarousel.querySelectorAll('.js-product__media-image img, .product__gallery-carousel-item .product__media-overlay')
        mobileImages.forEach((image, index) => {
          const newImage = image.cloneNode(true)
          image.parentNode.replaceChild(newImage, image)

          newImage.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()

            const productMedia = JSON.parse(this.querySelector('script[data-product-media]').textContent) || []
            const productTitle = document.querySelector('.product__info-container h3')?.textContent || 'Product'

            if (productMedia.length === 0) return

            const zoomModal = document.getElementById('zoom-modal')
            if (zoomModal && productMedia.length > 0) {
              zoomModal.open(productMedia, productTitle, index - 1)
            }
          })
        })
      }

      const desktopGallery = this.querySelector('.product__gallery-desktop')
      if (desktopGallery) {
        const desktopImages = desktopGallery.querySelectorAll('.product__gallery-item img.zoom-in, .product__gallery-item .product__media-overlay')
        desktopImages.forEach((image, index) => {
          const newImage = image.cloneNode(true)
          image.parentNode.replaceChild(newImage, image)

          newImage.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()

            const productMedia = JSON.parse(this.querySelector('script[data-product-media]').textContent) || []
            const productTitle = document.querySelector('.product__info-container h3')?.textContent || 'Product'

            if (productMedia.length === 0) return

            const zoomModal = document.getElementById('zoom-modal')
            if (zoomModal && productMedia.length > 0) {
              zoomModal.open(productMedia, productTitle, index)
            }
          })
        })
      }
    }, 100)
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-product-gallery', NovaProductGallery)
